import { useState } from 'react';
import useSWR from 'swr';
import { Settings, Lock } from 'lucide-react';
import { practicesApi, type PracticeRoom, type PracticePolicy } from '@/api/practices';
import { resolveEngineAuthContext } from '@/lib/auth';

export function PracticeAdminConsole() {
  // Partition SWR by active context so a tenant switch never renders old room data.
  const { activeTenantId } = resolveEngineAuthContext();
  const { data, error, mutate, isLoading } = useSWR(['practice-rooms', activeTenantId], practicesApi.list);
  const policy = useSWR(data ? ['practice-policy', activeTenantId] : null, practicesApi.policy);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  async function prepare() {
    setBusy(true);
    setNotice('');
    try {
      await practicesApi.create();
      await mutate();
      setNotice('Workshop preparation saved. The room is closed.');
    } catch {
      setNotice('Could not prepare the Workshop. Retry or reload.');
    } finally { setBusy(false); }
  }
  return (
    <section className="sp-widget m-4" aria-label="Practice Admin">
      <header className="sp-widget-header"><Settings size={16} /> Practice Admin</header>
      <div className="sp-widget-body space-y-4">
        <p>Prepare your Workshop. Rooms stay closed during V0 preparation.</p>
        {isLoading && <p role="status">Loading room preparation…</p>}
        {error && <p role="alert">Room ownership could not be verified. Only the tenant owner may administer rooms.</p>}
        {data && !data.rooms.length && <button className="sp-btn" disabled={busy} onClick={prepare}>Prepare Workshop</button>}
        {data?.rooms.map(room => <RoomEditor key={`${activeTenantId}:${room.id}:${room.version}`} room={room} onSaved={async () => { await mutate(); }} />)}
        {notice && <p role="status">{notice}</p>}
        <div className="flex items-center gap-2"><Lock size={16} /> Opening unavailable in V0</div>
        <p>Guest levels: look, comment, work. Doors: Stand ask-to-join and direct invite. Both require owner approval of the named person; neither admits guests in V0.</p>
        <p>Guests bring their own assistant. Named use of the owner assistant and all guest work remain OFF. No opening, comment or seat charge is introduced.</p>
        {policy.error && <p role="alert">Practice settings are unavailable.</p>}
        {policy.data && <>
          <h2>Effective operator settings</h2>
          <dl className="grid grid-cols-2 gap-2 text-sm">
            {Object.entries(policy.data.effective_settings).map(([key, value]) => <div key={key}>
              <dt>{key.replaceAll('_', ' ')}</dt><dd>{value === null ? 'Unset' : Array.isArray(value) ? value.join(', ') || 'None' : typeof value === 'boolean' ? value ? 'ON' : 'OFF' : String(value)}</dd>
            </div>)}
          </dl>
          {policy.data.can_edit && <PolicyEditor key={policy.data.version} policy={policy.data} onSaved={async () => { await policy.mutate(); }} />}
          <p>Operator policy version {policy.data.version}. Policy changes require verified Director authority. Activation needs a separately reviewed release.</p>
        </>}
      </div>
    </section>
  );
}

function RoomEditor({ room, onSaved }: { room: PracticeRoom; onSaved: () => Promise<void> }) {
  const [confirmation, setConfirmation] = useState(room.opening_confirmation);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  async function save() {
    setBusy(true);
    setNotice('');
    try {
      await practicesApi.save(room, confirmation);
      setNotice('Configuration saved. The room remains closed.');
      await onSaved();
    } catch {
      setNotice('Save failed. Reload to read current configuration before retrying.');
    } finally { setBusy(false); }
  }
  return <div className="space-y-3">
    <h2>Workshop — {room.state}</h2>
    <p>Preparation: {room.preparation}. Work integration: {room.integration}.</p>
    <dl>
      <dt>Method</dt><dd>{room.assistant_configuration.method_ref ?? 'Approved reference unavailable'}</dd>
      <dt>Domas</dt><dd>{room.assistant_configuration.doma_refs.join(', ') || 'Approved references unavailable'}</dd>
      <dt>Default model</dt><dd>{room.assistant_configuration.default_model_ref ?? 'Approved reference unavailable'}</dd>
    </dl>
    <p>No inference runs on save. Content stays with its owner; nothing is shared or deleted.</p>
    <label className="flex gap-2"><input type="checkbox" checked={confirmation} onChange={e => setConfirmation(e.target.checked)} />Confirm before opening this room</label>
    <button className="sp-btn" disabled={busy} onClick={save}>Save configuration</button>
    {notice && <p role="status">{notice}</p>}
  </div>;
}


// These settings record Director intent for later activation. The backend
// continues to enforce the closed V0 ceiling independently of this form.
function PolicyEditor({ policy, onSaved }: { policy: PracticePolicy; onSaved: () => Promise<void> }) {
  const [settings, setSettings] = useState(policy.settings);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const mutable = ['comment_tier_ref', 'comment_charge_enabled', 'seat_pricing_enabled', 'seat_price_ref', 'opening_charge_enabled', 'opening_price_ref', 'work_entitlement_ref', 'guest_work_enabled', 'granted_assistant_use_enabled', 'granted_assistant_payer'];
  async function save() {
    setBusy(true);
    try {
      await practicesApi.savePolicy({ ...policy, settings });
      setNotice('Operator settings saved. Activation remains deferred.');
      await onSaved();
    } catch { setNotice('Policy save failed. Reload before retrying.'); }
    finally { setBusy(false); }
  }
  return <fieldset className="space-y-2">
    <legend>Operator settings for later activation</legend>
    <p>These choices do not enable guests, opening or charges in V0.</p>
    {mutable.map(key => <label key={key} className="flex gap-2 items-center">
      <span>{key.replaceAll('_', ' ')}</span>
      {typeof settings[key] === 'boolean'
        ? <input type="checkbox" checked={Boolean(settings[key])} onChange={e => setSettings({ ...settings, [key]: e.target.checked })} />
        : key === 'granted_assistant_payer'
          ? <select value={String(settings[key] ?? '')} onChange={e => setSettings({ ...settings, [key]: e.target.value || null })}><option value="">Unset</option><option value="guest">Guest</option><option value="owner">Owner</option></select>
          : <input className="sp-input" value={String(settings[key] ?? '')} maxLength={255} onChange={e => setSettings({ ...settings, [key]: e.target.value || null })} />}
    </label>)}
    <label className="flex gap-2">Granted assistant actions
      <input className="sp-input" value={(settings.granted_assistant_actions as string[] ?? []).join(',')} onChange={e => setSettings({ ...settings, granted_assistant_actions: e.target.value ? e.target.value.split(',') : [] })} />
    </label>
    <label className="flex gap-2">Granted assistant duration (seconds)
      <input className="sp-input" type="number" min={1} max={86400} value={String(settings.granted_assistant_duration_seconds ?? '')} onChange={e => setSettings({ ...settings, granted_assistant_duration_seconds: e.target.value ? Number(e.target.value) : null })} />
    </label>
    <label className="flex gap-2">Requested room states
      <select value={(settings.reachable_open_states as string[] ?? ['closed']).join(',')} onChange={e => setSettings({ ...settings, reachable_open_states: e.target.value.split(',') })}>
        <option value="closed">Closed</option><option value="closed,early_access">Closed and early access</option><option value="closed,public">Closed and public</option><option value="closed,early_access,public">All states</option>
      </select>
    </label>
    <button className="sp-btn" disabled={busy} onClick={save}>Save operator settings</button>
    {notice && <p role="status">{notice}</p>}
  </fieldset>;
}
